import { useState, useEffect } from "react";

// Roster display preference: "list" (the table) or "gallery" (photo cards).
// Persisted like the theme and NSFW toggles, and broadcast via a CustomEvent so
// every mounted roster reacts at once.
const VIEW_KEY = "roster_view_mode";
const EVENT = "view-mode-changed";

export const VIEW_LIST = "list";
export const VIEW_GALLERY = "gallery";

// Gallery is the default; only an explicit "list" choice opts out. Same shape as the
// NSFW toggle, which defaults on unless the user actively picked SFW.
//
// This cannot leak photos to SFW visitors: the NSFW gate is applied separately by the
// caller, which forces the list whenever NSFW is off regardless of this preference.
export function readViewMode() {
  try {
    return localStorage.getItem(VIEW_KEY) === VIEW_LIST ? VIEW_LIST : VIEW_GALLERY;
  } catch {
    return VIEW_GALLERY;
  }
}

export function setViewMode(mode) {
  const next = mode === VIEW_GALLERY ? VIEW_GALLERY : VIEW_LIST;
  try {
    localStorage.setItem(VIEW_KEY, next);
  } catch {
    // Storage unavailable (private browsing); the choice just won't survive a reload.
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
}

export default function useViewMode() {
  const [mode, setMode] = useState(readViewMode);

  useEffect(() => {
    const onChanged = (e) => setMode(e.detail === VIEW_GALLERY ? VIEW_GALLERY : VIEW_LIST);
    setMode(readViewMode());
    window.addEventListener(EVENT, onChanged);
    return () => window.removeEventListener(EVENT, onChanged);
  }, []);

  return mode;
}
